/**
 * Arcade sprite font renderer (CraftPix Classic Sevens alphabets).
 * Use for marquee / win amounts / SPIN chrome — not long body paragraphs.
 */
import React, { useMemo } from "react";
import {
  type SevensFontColor,
  sevensGlyphUrl,
} from "@/lib/sprite-fonts";

export type SpriteFontProps = {
  text: string;
  /** Pack color */
  color?: SevensFontColor;
  /** Glyph height in CSS px (width auto-scales from native aspect) */
  height?: number;
  /** Gap between glyphs as fraction of height (0.02–0.15) */
  tracking?: number;
  /** Space width as fraction of height */
  spaceWidth?: number;
  className?: string;
  /** Accessibility / SEO text (defaults to text) */
  ariaLabel?: string;
  /** Uppercase force */
  upper?: boolean;
  /** Max width — scales glyphs down if overflow risk (approx) */
  maxWidth?: number;
};

export function SpriteFont({
  text,
  color = "yellow",
  height = 28,
  tracking = 0.04,
  spaceWidth = 0.35,
  className = "",
  ariaLabel,
  upper = true,
  maxWidth,
}: SpriteFontProps) {
  const display = upper ? String(text ?? "").toUpperCase() : String(text ?? "");

  const glyphs = useMemo(() => {
    const out: Array<
      | { type: "space"; key: string }
      | { type: "glyph"; key: string; src: string; ch: string }
      | { type: "fallback"; key: string; ch: string }
    > = [];
    let i = 0;
    for (const ch of display) {
      if (ch === " " || ch === "\t") {
        out.push({ type: "space", key: `sp-${i++}` });
        continue;
      }
      const src = sevensGlyphUrl(ch, color);
      if (src) out.push({ type: "glyph", key: `g-${i++}-${ch}`, src, ch });
      else out.push({ type: "fallback", key: `f-${i++}-${ch}`, ch });
    }
    return out;
  }, [display, color]);

  // Rough fit: each glyph ~0.9 * height wide
  const est = glyphs.reduce((w, g) => {
    if (g.type === "space") return w + height * spaceWidth;
    return w + height * 0.72 + height * tracking;
  }, 0);
  const scale =
    maxWidth && est > maxWidth ? Math.max(0.45, maxWidth / est) : 1;
  const h = Math.round(height * scale);
  const gap = Math.max(0, Math.round(h * tracking));
  const sp = Math.round(h * spaceWidth);

  return (
    <span
      className={`sprite-font sprite-font--sevens ${className}`}
      role="img"
      aria-label={ariaLabel ?? display}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "nowrap",
        gap: `${gap}px`,
        lineHeight: 0,
        userSelect: "none",
      }}
    >
      {glyphs.map((g) => {
        if (g.type === "space") {
          return (
            <span
              key={g.key}
              aria-hidden
              style={{ width: sp, height: h, flexShrink: 0 }}
            />
          );
        }
        if (g.type === "fallback") {
          return (
            <span
              key={g.key}
              aria-hidden
              style={{
                height: h,
                minWidth: h * 0.4,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Bebas Neue, Impact, sans-serif",
                fontSize: h * 0.85,
                color: "#fbbf24",
                fontWeight: 700,
              }}
            >
              {g.ch}
            </span>
          );
        }
        return (
          <img
            key={g.key}
            src={g.src}
            alt=""
            draggable={false}
            height={h}
            style={{
              height: h,
              width: "auto",
              maxHeight: h,
              objectFit: "contain",
              flexShrink: 0,
              imageRendering: "auto",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.75))",
            }}
            loading="lazy"
            decoding="async"
          />
        );
      })}
    </span>
  );
}

/** Compact win amount e.g. +1250 */
export function SpriteWinAmount({
  amount,
  color = "yellow",
  height = 32,
}: {
  amount: number | string;
  color?: SevensFontColor;
  height?: number;
}) {
  const t =
    typeof amount === "number"
      ? `+${Math.round(amount).toLocaleString("en-US").replace(/,/g, "")}`
      : String(amount);
  return (
    <SpriteFont text={t} color={color} height={height} upper tracking={0.02} />
  );
}
